import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { TrendingUp, TrendingDown, Wallet, Percent, Building2 } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, PieChart, Pie, Cell,
} from 'recharts';

const fmt = (n) => `${Number(n || 0).toLocaleString('ru-RU')} ₸`;
const fmtShort = (n) => {
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${Math.round(n / 1e3)}k`;
  return `${n}`;
};
const PIE_COLORS = ['#059669', '#0ea5e9', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#ec4899', '#64748b', '#a3a3a3'];

function iso(d) { return d.toISOString().slice(0, 10); }
function firstOfMonth(off = 0) { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + off); return d; }
function endOfMonth(off = 0) { const d = new Date(); d.setMonth(d.getMonth() + off + 1, 0); return d; }

const PRESETS = [
  { key: 'this_month', label: 'Текущий месяц', range: () => [iso(firstOfMonth(0)), iso(new Date())] },
  { key: 'last_month', label: 'Прошлый месяц', range: () => [iso(firstOfMonth(-1)), iso(endOfMonth(-1))] },
  { key: 'quarter', label: '3 месяца', range: () => [iso(firstOfMonth(-2)), iso(new Date())] },
  { key: 'year', label: 'Год', range: () => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return [iso(d), iso(new Date())]; } },
];

function AdminFinancePnl({ branchId, branches }) {
  const [preset, setPreset] = useState('this_month');
  const [range, setRange] = useState(PRESETS[0].range());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyPreset = (key) => {
    setPreset(key);
    const p = PRESETS.find(x => x.key === key);
    if (p) setRange(p.range());
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ branch_id: branchId, date_from: range[0], date_to: range[1] });
      const r = await axios.get(`${API}/finance/pnl?${p}`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch { toast.error('Не удалось загрузить P&L'); }
    finally { setLoading(false); }
  }, [branchId, range]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals;
  const profitPositive = (t?.profit || 0) >= 0;

  return (
    <div data-testid="finance-pnl-tab">
      {/* Period presets */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {PRESETS.map(p => (
          <button key={p.key} onClick={() => applyPreset(p.key)}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${preset === p.key ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            data-testid={`pnl-preset-${p.key}`}>{p.label}</button>
        ))}
        <div className="flex items-center gap-1 ml-auto">
          <Input type="date" value={range[0]} onChange={e => { setPreset('custom'); setRange([e.target.value, range[1]]); }} className="w-[150px]" data-testid="pnl-date-from" />
          <span className="text-gray-400">—</span>
          <Input type="date" value={range[1]} onChange={e => { setPreset('custom'); setRange([range[0], e.target.value]); }} className="w-[150px]" data-testid="pnl-date-to" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-400" data-testid="pnl-loading">Загрузка…</div>
      ) : t && (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <Kpi icon={TrendingUp} tint="emerald" label="Выручка" value={fmt(t.revenue)} testid="pnl-revenue" />
            <Kpi icon={TrendingDown} tint="red" label="Расходы" value={fmt(t.expenses)} testid="pnl-expenses" />
            <Kpi icon={Wallet} tint={profitPositive ? 'emerald' : 'red'} label="Прибыль" value={fmt(t.profit)} testid="pnl-profit" />
            <Kpi icon={Percent} tint={profitPositive ? 'blue' : 'red'} label="Маржа" value={`${t.margin}%`} testid="pnl-margin" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            {/* Trend chart */}
            <Card className="xl:col-span-2"><CardContent className="p-4">
              <div className="text-sm font-semibold text-gray-800 mb-3">Доходы и расходы {data.period.granularity === 'month' ? '(по месяцам)' : '(по дням)'}</div>
              {data.series.length === 0 ? (
                <div className="text-center text-gray-400 py-16">Нет данных за период</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={data.series} margin={{ left: 4, right: 8, top: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={44} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Legend />
                    <Bar dataKey="revenue" name="Выручка" fill="#059669" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="expenses" name="Расходы" fill="#ef4444" radius={[3, 3, 0, 0]} />
                    <Line dataKey="profit" name="Прибыль" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </CardContent></Card>

            {/* Expense breakdown */}
            <Card><CardContent className="p-4">
              <div className="text-sm font-semibold text-gray-800 mb-3">Структура расходов</div>
              {data.expense_by_category.length === 0 ? (
                <div className="text-center text-gray-400 py-16">Нет расходов</div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={data.expense_by_category} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={75} innerRadius={40}>
                        {data.expense_by_category.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1 mt-2">
                    {data.expense_by_category.slice(0, 6).map((e, i) => (
                      <div key={e.category} className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{e.category}</span>
                        <span className="font-medium">{fmt(e.amount)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent></Card>
          </div>

          {/* By branch */}
          <Card className="mt-4"><CardContent className="p-0 overflow-x-auto">
            <div className="px-4 py-3 text-sm font-semibold text-gray-800 flex items-center gap-2"><Building2 className="w-4 h-4" /> P&L по филиалам</div>
            <table className="w-full text-sm" data-testid="pnl-branch-table">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Филиал</th>
                  <th className="text-right px-4 py-2">Выручка</th>
                  <th className="text-right px-4 py-2">Расходы</th>
                  <th className="text-right px-4 py-2">Прибыль</th>
                  <th className="text-right px-4 py-2">Маржа</th>
                </tr>
              </thead>
              <tbody>
                {data.by_branch.map((b, i) => (
                  <tr key={b.branch_id || i} className="border-t" data-testid={`pnl-branch-row-${i}`}>
                    <td className="px-4 py-2 font-medium">{b.branch_name}</td>
                    <td className="px-4 py-2 text-right">{fmt(b.revenue)}</td>
                    <td className="px-4 py-2 text-right text-red-600">{fmt(b.expenses)}</td>
                    <td className={`px-4 py-2 text-right font-bold ${b.profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(b.profit)}</td>
                    <td className="px-4 py-2 text-right">{b.margin}%</td>
                  </tr>
                ))}
                {data.by_branch.length === 0 && <tr><td colSpan={5} className="text-center text-gray-400 py-8">Нет данных</td></tr>}
              </tbody>
            </table>
          </CardContent></Card>

          <p className="text-[11px] text-gray-400 mt-3">Выручка учитывается по дате заказа (оплаченные заказы). Закупки ингредиентов учитываются как расход «Общий» (склад не привязан к филиалу).</p>
        </>
      )}
    </div>
  );
}

const TINTS = { emerald: 'text-emerald-700 bg-emerald-50', red: 'text-red-700 bg-red-50', blue: 'text-blue-700 bg-blue-50' };
function Kpi({ icon: Icon, tint, label, value, testid }) {
  return (
    <Card data-testid={testid}><CardContent className="p-4 flex items-start gap-3">
      <div className={`p-2 rounded-lg ${TINTS[tint]}`}><Icon className="w-5 h-5" /></div>
      <div><div className="text-xs text-gray-500">{label}</div><div className="text-xl font-bold text-gray-900">{value}</div></div>
    </CardContent></Card>
  );
}

export default AdminFinancePnl;
